import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { ItemComponent } from "./ItemComponent";
import { characterStyles } from "./CharacterComponent";
import { MixChemicalsComponent } from "./MixChemicalsComponent";
import { LetterComponent } from "./LetterComponent";
import { InventoryComponent } from "./InventoryComponent";
import { MorsePuzzleComponent } from "./MorsePuzzleComponent";
import { stat } from "fs";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        height: 100vh;
        display: grid;
        overflow: hidden;
    }

    .header {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        z-index: 0;
    }

    .header img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        image-rendering: pixelated;
    }

    .title, .content {
        position: absolute;
        width: 80%;
        z-index: 1;
    }
    
    .content {
        position: absolute;
        bottom: 0;
        width: 100%;
        background-color: #1D2434;
        opacity: 85%;
        display: flex;
        flex-direction: column;
        height: fit-content;
    }

    .content > div:first-child {
        border: 2px #3B4B62 solid;
    }

    .content .button {
        padding: 5px 10px;
        text-transform: uppercase;
        cursor: pointer;
        user-select: none;
        color: #fff;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        margin: 10px 15px 0 15px;
        display: flex;
        height: auto;
        justify-content: flex-start;
        align-items: center;
    }

    .footer .buttons {
        display: flex;
        flex-direction: row;
        gap: 30px;
        align-items: flex-start;
        margin: 10px;
    }

    .action-buttons {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-height: 80px;
        overflow-y: auto;
        padding-right: 10px;
        scrollbar-width: thin;
    }

    .footer .button {
        padding: 15px 20px;
        margin: 5px 0;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        color: #78808D;
        border: 3px #3B4B62 solid;
        border-radius: 10px;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #fff9;
    }
`;

const startupstyles: string = css`


.startcontent {
    position: absolute;
     bottom: auto;
     width: 100%;
     display: flex;
     flex-direction: column;
     height: fit-content;
 }

 .startcontent .button {
     padding: 10px 30px;
     text-transform: uppercase;
     cursor: pointer;
     user-select: none;
     color: #fff;
 }


 .startfooter {
     border-radius: 10px 10px 0 0;
     margin-top: 10px;
     display: flex;
     height: 100vh;
     justify-content: flex-start;
     align-items: center;
 }

 .startfooter .buttons {
     display: flex;
     flex-direction: column;
     gap: 15px;
     align-items: flex-start; 
     margin: 20px;
 } 


 .startfooter .button {
     margin: 5px 0; 
     text-transform: uppercase;
     cursor: pointer;
     display: inline-block;
     user-select: none;
     color: #fff;
 }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    /** Current active item component */

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Event listener één keer toevoegen bij het verbinden van de component
        document.addEventListener("keypress", this.toggleInventory.bind(this));

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();

        // Refresh inventory
        this.updateInventory();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
                ${startupstyles}
                ${characterStyles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
           <game-item></game-item>
          
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.applyTypingAnimation();
        this.initializeInventory();
        this.initializeItem();
    }

    /**
     * Initialize the inventory for the current game state
     */
    private initializeInventory(): void {
        const inventoryElement: InventoryComponent = this.shadowRoot?.querySelector("game-inventory") as InventoryComponent;
        inventoryElement.inventory = this._currentGameState?.inventory || [];
    }

    /**
     * Update inventory for the current game state
     */
    private updateInventory(): void {
        const inventoryElement: InventoryComponent = this.shadowRoot?.querySelector("game-inventory") as InventoryComponent;
        inventoryElement.inventory = this._currentGameState?.inventory || [];
    }

    private initializeItem(): void {
        const inventoryElement: ItemComponent = this.shadowRoot?.querySelector("game-item") as ItemComponent;
        inventoryElement.item = this._currentGameState?.items || [];
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header" style="position: relative;">
                    ${roomImages.map((url, index) => `
                        <img src="/assets/img/rooms/${url}.png" 
                             style="position: absolute; top: 0; left: 0; z-index: ${index === 0 ? 1 : 0};" />
                    `).join("")}
                </div>
    
                <game-inventory style="display: none;"></game-inventory>
                <game-item style="display: none;"></game-item>

                <mini-game style="display: none;"></mini-game>
                <mix-chemicals style="display: none;"></mix-chemicals>
                <memory-game style="display: none;"></memory-game>

                <morse-puzzle style="display: none;"></morse-puzzle>
                <numeric-lock style="display: none;"></numeric-lock>



            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        const roomAlias: string | undefined = this._currentGameState?.roomAlias;

        if (roomAlias === "startup") {
            return `
            <div class="startcontent">
            ${this._currentGameState?.text.map(text => `<div><p>${text}</p></div>`).join("") || ""}
        `;
        }
        if (roomAlias === "charactersroom") {
            return `
            <div class="character-content">
            ${this._currentGameState?.text.map(text => `<div><p>${text}</p></div>`).join("") || ""}
            `;
        }
        else {
            return `
            <div class="content">
            ${this._currentGameState?.text.map(text => `<div><p>${text}</p></div>`).join("") || ""}
        `;
        }
    }

    /**
     * Apply typing animation to the content text
     */
    private applyTypingAnimation(): void {
        const elements = this.shadowRoot?.querySelectorAll(".typing");
        if (!elements) return;

        elements.forEach(element => {
            const text = element.textContent || "";
            element.textContent = "";
            let index = 0;

            const interval = setInterval(() => {
                if (index < text.length) {
                    element.textContent += text.charAt(index);
                    index++;
                } else {
                    clearInterval(interval);
                }
            }, 20);
        });
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        const roomAlias: string | undefined = this._currentGameState?.roomAlias;
        if (roomAlias === "startup") {
            return html`
            <div class="startfooter">
                <div class="buttons">
                    
                    ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}

                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
            `;
        }
        if (roomAlias === "charactersroom") {
            return html`
            <div class="character-footer">
                <div class="buttons">
                    
                    ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}

                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
            `;
        }
        else {
            return html`
            <div class="footer">
                <div class="buttons">

                    ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}

                    <div class="action-buttons">
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
        }
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();
            this.render();
            return;
        }

        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);
        if (!state) return;

        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }

        this.updateGameState(state);

        if (state.text.some(t => t.includes("Follow the order of the rainbow to mix the liquids."))) {
            const mix: MixChemicalsComponent = this.shadowRoot?.querySelector("mix-chemicals") as MixChemicalsComponent;
            if (mix) {
                mix.style.display = "flex";
                mix.addEventListener("mix-success", () => this.unlockNextRoom(), { once: true });
            }
        }

        if (state.text.some(t => t.includes("Translate the Morse code"))) {
            const morse: MorsePuzzleComponent = this.shadowRoot?.querySelector("morse-puzzle") as MorsePuzzleComponent;
            if (morse) {
                morse.style.display = "flex";
                morse.addEventListener("morse-puzzle-solved", () => this.unlockNextRoom(), { once: true });
            }
        }

        if (state.text.some(t => t.includes("Open lock"))) {
            const numericLock: HTMLElement = this.shadowRoot?.querySelector("numeric-lock") as HTMLElement;
            if (numericLock) {
                numericLock.style.display = "flex";
                numericLock.addEventListener("password-success", () => this.unlockNextRoom(), { once: true });
            }
        }
        if (state.text?.some(t => t.includes("Start memory game"))) {
            const memory = this.shadowRoot?.querySelector("memory-game") as MixChemicalsComponent;
            if (memory) {
                memory.style.display = "flex";
                memory.addEventListener("memory-success", () => this.unlockNextRoom(), { once: true });
            }
        }

        if (button.showItemComponent) {
            this.toggleItemComponent(button.id, button.name);
        }
    }

   /**
     * Unlocks the next room by adding the "next-room" action to the current game state if it doesn't already exist.
     */
    private unlockNextRoom(): void {
        if (!this._currentGameState) return;

        const alreadyExists = this._currentGameState.actions.some(a => a.alias === "next-room");
        if (!alreadyExists) {
            this._currentGameState.actions.push({ alias: "next-room", name: "Next Room" });
            this.render();
        }
    }



    /** toggle the item component */
    private toggleItemComponent(itemId?: number, itemName?: string): void {
        const itemComponent: ItemComponent = this.shadowRoot?.querySelector("game-item") as ItemComponent;

        if (itemComponent) {
            itemComponent.toggle();

            if (itemId && itemName) {
                const cleanName = itemName.replace(/^Look at\s+/, "");

                itemComponent.item = [{ alias: "selected", name: cleanName, id: itemId }];
            }
        }
        else {
            console.log("ItemComponent not found");
        }
    }

    /**
     * Toggle de zichtbaarheid van de inventory bij het indrukken van "i"
     */
    private toggleInventory(event: KeyboardEvent): void {
        if (event.key === "i" && this.shadowRoot) {
            const inventory: InventoryComponent | null = this.shadowRoot.querySelector("game-inventory");
            if (inventory) {
                if (inventory.style.display === "none") {
                    inventory.style.display = "flex";
                }
                else {
                    inventory.style.display = "none";
                }
            }
        }
    }

    // Toggle de zichtbaarheid van de minigame bij het indrukken van "z"
    private toggleMiniGame(event: KeyboardEvent): void {
        if (event.key === "z" && this.shadowRoot) {
            const miniGame: HTMLElement | null = this.shadowRoot.querySelector("mini-game");
            if (miniGame) {
                if (miniGame.style.display === "none") {
                    miniGame.style.display = "flex";
                }
                else {
                    miniGame.style.display = "none";
                }
            }
        }
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
